import {LazyResponse} from "../types/types";
import axios from "axios";
import {mutate} from "swr";
import {FetchDataParams} from "react-ive-tables";
import {parseDateFilter} from "../common";
import {FilterService} from "primereact/api";
import cloneDeep from "lodash.clonedeep";


export interface LazyFetchingServiceParams {
    key: string;
}

export class LazyFetchingService<T,K = never> {
    private readonly key: string;
    private limit: number = 20;
    private offset: number = 0;
    private filters: any = {};
    private sort: any = undefined;

    constructor(key: string) {
        this.key = key;
        this.getLazyFetcher = this.getLazyFetcher.bind(this);
        this.getLazyFetcherWithInitialFilters = this.getLazyFetcherWithInitialFilters.bind(this);
        this.getDataFetcher = this.getDataFetcher.bind(this);
        this.clearState = this.clearState.bind(this);
    }

    clearState() {
        this.limit = 20;
        this.offset = 0;
        this.filters = {};
        this.sort = undefined;
        mutate(this.key);
    }

    getFilters() {
        return cloneDeep(this.filters);
    }

    getLazySortableFetcher(sortConverter: (event?: any) => any, initialFilters?: Record<string, string>) : (...args: any) => Promise<LazyResponse<T, K>> {
        return async (...args: any): Promise<LazyResponse<T, K>> => {
            try {
                const parsedSort = sortConverter(this.sort);
                const params = args[0];
                const res = await axios.post<LazyResponse<T, K>>(params[0], {
                    filters: initialFilters ? {...this.filters, ...initialFilters} : this.filters,
                    offset: this.offset,
                    limit: this.limit,
                    ...parsedSort
                }, {data: {isSWR: params[1]}});
                return res.data;
            } catch (err) {
                return {rows: [], totalRecords: 0};
            }
        }
    }

    getLazyFetcherWithInitialFilters(initialFilters: Record<string, any>) : (...args: any) => Promise<LazyResponse<T, K>>{
        return async (...args: any) => {
            try {
                const params = args[0];
                const res = await axios.post<LazyResponse<T, K>>(params[0], {
                    filters: {...this.filters, ...initialFilters},
                    offset: this.offset,
                    limit: this.limit
                }, {data: {isSWR: params[1]}});
                return res.data;
            } catch (err) {
                return {rows: [], totalRecords: 0};
            }
        }
    }

    async getLazyFetcher(...args: any): Promise<LazyResponse<T, K>> {
        try {
            const params = args[0];
            const res = await axios.post<LazyResponse<T, K>>(params[0], {
                filters: this.filters,
                offset: this.offset,
                limit: this.limit
            }, {data: {isSWR: params[1]}});
            return res.data;
        } catch (err) {
            return {rows: [], totalRecords: 0};
        }
    }

    async getDataFetcher({offset, limit, filters, sort} : FetchDataParams) : Promise<void> {
        const parsedFilters = Object.keys(filters).reduce((acc: any, key: string) => {
            //Handle date filter, because the visualization of the column is inverted
            if(key === "date" && filters[key].value !== undefined && filters[key].value !== null)  {
                return {...acc, [key]: parseDateFilter(filters[key].value)}
            }

            //Handle multiselect with empty array.
            if(Array.isArray(filters[key].value) && filters[key].value.length === 0) return acc;

            if (filters[key].value !== null && filters[key].value !== '' && filters[key].value !== undefined)
                return {...acc, [key]: String(filters[key].value)}
            return acc
        }, {});

        this.limit = limit;
        this.offset = offset;
        this.filters = parsedFilters;
        this.sort = sort;
        await mutate(this.key);
    }
}
